import type { GraphQLError } from "graphql/error"
import { shallow } from "zustand/shallow"
import { createWithEqualityFn } from "zustand/traditional"

import type {
  FetchUserInfo_OrganizationFragment,
  FetchUserInfo_UserFragment,
} from "#gql/graphql.js"
import { getFragmentData, graphql } from "#gql/index.js"
import { authClient } from "#lib/auth-client.js"
import { client } from "#utils/graphql-client.ts"

export const FetchUserInfo_UserFragmentDocument = graphql(/* GraphQL */ `
  fragment fetchUserInfo_User on User {
    id
    email
    name
    emailVerified
    phone
    role
    image
    createdAt
    updatedAt
  }
`)

export const FetchUserInfo_OrganizationFragmentDocument = graphql(/* GraphQL */ `
  fragment fetchUserInfo_Organization on Organization {
    id
    name
    slug
    logo
    metadata
  }
`)

const FetchUserInfo_MeLoggedInFragmentDocument = graphql(/* GraphQL */ `
  fragment fetchUserInfo_MeLoggedIn on MeLoggedIn {
    isLoggedIn
    activeInvite
    user {
      ...fetchUserInfo_User
    }
    organization {
      ...fetchUserInfo_Organization
    }
  }
`)

const MeQuery = graphql(/* GraphQL */ `
  query fetchUserInfo_Me {
    me {
      __typename
      ... on MeLoggedIn {
        ...fetchUserInfo_MeLoggedIn
      }
      ... on MeNotLoggedIn {
        __typename
      }
    }
  }
`)

const fetchMe = async () => {
  const sessionUser = await authClient.getSession()
  if (sessionUser.data?.user) {
    return client.request(MeQuery).catch((data: unknown) => {
      if (data instanceof Object && "request" in data && "response" in data) {
        // @ts-expect-error graphql-request doesn't export types, so we hack around it
        const errors = data.response?.errors as GraphQLError[] | undefined
        if (errors?.[0]?.extensions?.code === "UNAUTHORIZED") {
          return null
        }
      }
      throw data
    })
  }
  return null
}

interface Me {
  isLoggedIn: boolean
  activeInvite: boolean
  user: FetchUserInfo_UserFragment
  organization: FetchUserInfo_OrganizationFragment
}

export type MeStoreBase = {
  fetchUserInfo: () => Promise<MeStoreLoggedIn | MeStoreNotLoggedIn>
}

export type MeStoreLoggedIn = {
  state: "LoggedIn"
  me: Me
}

export type MeStoreNotLoggedIn = {
  state: "NotLoggedIn"
  me: null
}

export type MeStoreInit = {
  state: "Init"
  me: null
}

type MeStore = MeStoreBase & (MeStoreLoggedIn | MeStoreNotLoggedIn | MeStoreInit)

export const useUserInfo = createWithEqualityFn<MeStore>((set) => {
  const fetchUserInfo = async (): Promise<MeStoreLoggedIn | MeStoreNotLoggedIn> => {
    const data = await fetchMe()
    const calculateState = (): MeStoreLoggedIn | MeStoreNotLoggedIn => {
      switch (data?.me?.__typename) {
        case "MeLoggedIn": {
          const raw = getFragmentData(FetchUserInfo_MeLoggedInFragmentDocument, data.me)
          const user = getFragmentData(FetchUserInfo_UserFragmentDocument, raw.user)
          const organization = getFragmentData(
            FetchUserInfo_OrganizationFragmentDocument,
            raw.organization,
          )
          return {
            state: "LoggedIn",
            me: {
              isLoggedIn: raw.isLoggedIn,
              activeInvite: raw.activeInvite,
              user,
              organization,
            },
          }
        }
        default:
          return { state: "NotLoggedIn", me: null }
      }
    }
    const state = calculateState()
    set(state)
    return state
  }

  void fetchUserInfo()

  return {
    state: "Init",
    me: null,
    fetchUserInfo,
  }
}, shallow)

export const useLoggedInUserInfo = () => {
  const userInfo = useUserInfo()
  if (userInfo.state === "LoggedIn") {
    return userInfo
  }
  throw new Error(
    "User is not logged in, you should only run this hook inside components that are rendered in an authenticated context",
  )
}
